/**
 * <manifesto-portal>
 *
 * GSAP ScrollTrigger owner for the pinned manifesto chapter (desktop).
 *
 * Choreography (scroll distances)
 *   curtain  the section before this one is held still while this chapter
 *            slides up over it, released once it is fully covered.
 *   travel   the element's own height. The statement is read, still; the
 *            next section rises unseen behind the opaque overlay
 *            (pinSpacing: false). Its top reaches the viewport top after
 *            exactly the travel; the statement lifts away just before that.
 *   open     the next section is pinned beneath, motionless; the drop opens
 *            onto it through a half-strength Beige wash that thins to
 *            nothing as the circle spreads past the viewport corners.
 *   landing  the next section holds a little longer, clear of the header,
 *            then eases into flow. Total pinned scroll S = travel + open.
 *
 * Once per page load
 *   When the landing ends, the chapter settles: every trigger is killed,
 *   the element takes its still form (data-mp-static) and the scroll
 *   position is compensated in the same frame so nothing on screen moves.
 *   Scrolling back up then shows the still page. A fresh load or a reload
 *   plays the choreography again; a load that restores a position beyond
 *   the chapter settles at once.
 *
 * Phones (under 750px) always get the still page: fixed pins flicker when
 * the address bar moves, and a flawed signature costs more than none.
 *
 * Lifecycle: connectedCallback creates a fresh AbortController; the load
 * listener hangs on its signal. disconnectedCallback kills every trigger
 * and tween, then aborts the controller.
 *
 * Theme editor (window.Shopify.designMode): still page, nothing pinned.
 */
class ManifestoPortal extends HTMLElement {
  /** @type {AbortController | null} */
  #lifetime = null;

  /** @type {any} */
  #timeline = null;

  /** @type {any} */
  #trigger = null;

  /** @type {any} */
  #nextPin = null;

  /** @type {any} */
  #prevPin = null;

  /** @type {any} */
  #landing = null;

  /** @type {HTMLElement | null} */
  #landingTarget = null;

  /** @type {HTMLElement | null} */
  #prev = null;

  /** @type {HTMLElement | null} */
  #next = null;

  /** Extra growth beyond the viewport diagonal, so the ring's edge is never seen. */
  static HOLE_MARGIN = 1.3;

  connectedCallback() {
    this.#lifetime?.abort();
    this.#lifetime = new AbortController();
    const { signal } = this.#lifetime;

    if (window.Shopify && window.Shopify.designMode) {
      this.setAttribute('data-design-mode', '');
      return;
    }
    this.removeAttribute('data-design-mode');

    if (window.matchMedia('(max-width: 749px)').matches) {
      this.setAttribute('data-mp-static', '');
      return;
    }

    if (window.gsap && window.ScrollTrigger) {
      this.#build();
      return;
    }

    window.addEventListener(
      'load',
      () => {
        if (window.gsap && window.ScrollTrigger) {
          this.#build();
        } else {
          this.setAttribute('data-mp-static', '');
        }
      },
      { once: true, signal }
    );
  }

  disconnectedCallback() {
    this.#teardown();
    this.#lifetime?.abort();
    this.#lifetime = null;
  }

  /** Kills every trigger and tween and clears what they wrote. */
  #teardown() {
    this.#landing?.scrollTrigger?.kill();
    this.#landing?.kill();
    this.#landing = null;
    if (this.#landingTarget) {
      window.gsap?.set(this.#landingTarget, { clearProps: 'transform' });
      this.#landingTarget = null;
    }
    this.#prevPin?.kill();
    this.#prevPin = null;
    if (this.#prev) {
      this.#prev.style.removeProperty('z-index');
      this.#prev = null;
    }
    this.#nextPin?.kill();
    this.#nextPin = null;
    this.#trigger?.kill();
    this.#trigger = null;
    this.#timeline?.kill();
    this.#timeline = null;
    this.removeAttribute('data-mp-ready');
    this.removeAttribute('data-mp-open');
    this.style.removeProperty('--mp-hole');
    this.style.removeProperty('--mp-veil');
    const stage = this.querySelector('[data-mp-stage]');
    const statement = this.querySelector('[data-mp-statement]');
    const arch = this.querySelector('[data-mp-arch]');
    if (window.gsap) {
      if (stage) window.gsap.set(stage, { clearProps: 'pointerEvents' });
      if (statement) window.gsap.set(statement, { clearProps: 'opacity,transform' });
      if (arch) window.gsap.set(arch, { clearProps: 'opacity' });
    }
  }

  /**
   * The chapter has been seen: become the still page without moving what
   * the visitor is looking at.
   */
  #settle() {
    const { ScrollTrigger } = window;
    const anchor = this.#next || this;
    const before = anchor.getBoundingClientRect().top;

    this.#teardown();
    this.setAttribute('data-mp-static', '');
    ScrollTrigger?.refresh(true);

    const after = anchor.getBoundingClientRect().top;
    const delta = after - before;
    if (Math.abs(delta) > 0.5) {
      const y = window.scrollY + delta;
      if (window.tgLenis) {
        window.tgLenis.scrollTo(y, { immediate: true, force: true });
      } else {
        window.scrollTo(0, y);
      }
    }
  }

  #build() {
    const { gsap, ScrollTrigger } = window;
    gsap.registerPlugin(ScrollTrigger);
    ScrollTrigger.config({ ignoreMobileResize: true });

    const pin = this.querySelector('[data-mp-pin]');
    const veil = this.querySelector('[data-mp-veil]');
    const stage = this.querySelector('[data-mp-stage]');
    const arch = this.querySelector('[data-mp-arch]');
    const statement = this.querySelector('[data-mp-statement]');
    if (!pin || !veil || !stage || !arch || !statement) return;

    const section = this.closest('.shopify-section');
    const next = /** @type {HTMLElement | null} */ (section?.nextElementSibling);
    const prev = /** @type {HTMLElement | null} */ (section?.previousElementSibling);
    this.#next = next;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const scrub = reduced ? true : Number(this.dataset.scrub) || 0.6;

    const viewport = () => window.innerHeight || document.documentElement.clientHeight;
    const openDistance = () => viewport() * (Number(this.dataset.open) || 0.6);
    const dwellDistance = () => viewport() * (Number(this.dataset.dwell) || 0);
    const headerHeight = () =>
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--tg-header-height')) || 0;
    const total = () => this.offsetHeight + openDistance();

    const travel = this.offsetHeight;
    const vh = viewport();
    const S = total();
    const fadeStart = (travel - vh * 0.24) / S;
    const fadeEnd = (travel - vh * 0.02) / S;
    const openStart = travel / S;
    const openLen = 1 - openStart;

    // The drop must spread past the viewport's corners: scale so its
    // diameter exceeds the diagonal, whatever the screen.
    const diameter = arch.getBoundingClientRect().width || 1;
    const diagonal = Math.hypot(window.innerWidth, vh);
    const holeMax = (diagonal / diameter) * ManifestoPortal.HOLE_MARGIN;

    const tl = gsap.timeline({ paused: true, defaults: { ease: 'none' } });
    tl.to(statement, { opacity: 0, y: -20, duration: fadeEnd - fadeStart, ease: 'power1.in' }, fadeStart);
    tl.set(this, { '--mp-hole': 1 }, openStart);
    tl.to(arch, { opacity: 0, duration: openLen * 0.12 }, openStart);
    // The drop spreads past the corners; through it the real section is seen
    // through a wash that thins to nothing. Both finish at 80% of the
    // opening: the rest is a buffer for the scrub to catch up, so the pin
    // never releases on a half-open door.
    tl.set(this, { '--mp-veil': 0.55 }, openStart);
    tl.to(this, { '--mp-hole': holeMax, duration: openLen * 0.8, ease: 'power2.in' }, openStart);
    tl.to(this, { '--mp-veil': 0, duration: openLen * 0.7, ease: 'power1.inOut' }, openStart);
    tl.eventCallback('onUpdate', () => this.toggleAttribute('data-mp-open', tl.progress() >= openStart - 0.0005));
    tl.set(stage, { pointerEvents: 'auto' }, 0.001);
    tl.set(stage, { pointerEvents: 'none' }, 0.999);
    this.#timeline = tl;

    this.setAttribute('data-mp-ready', '');

    this.#trigger = ScrollTrigger.create({
      trigger: this,
      start: 'top top',
      end: () => `+=${total()}`,
      pin,
      pinSpacing: false,
      anticipatePin: 1,
      scrub,
      animation: tl,
      invalidateOnRefresh: true,
    });

    // The curtain: the section before this one holds still, beneath, while
    // the chapter slides up over it, and is released once fully covered.
    if (prev) {
      this.#prev = prev;
      prev.style.zIndex = '1';
      this.#prevPin = ScrollTrigger.create({
        trigger: prev,
        start: 'top top',
        end: 'bottom top',
        pin: true,
        pinSpacing: false,
        anticipatePin: 1,
        invalidateOnRefresh: true,
      });
    }

    // The landing: the next section holds still through the opening and
    // for a dwell after the light clears. When the landing ends, the
    // chapter has been seen and settles into its still form.
    const settle = () => this.#settle();
    if (next) {
      this.#nextPin = ScrollTrigger.create({
        trigger: next,
        start: 'top top',
        end: () => `+=${openDistance() + dwellDistance()}`,
        pin: true,
        pinSpacing: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onLeave: settle,
      });

      const inner = /** @type {HTMLElement | null} */ (next.querySelector('.section') || next.firstElementChild);
      if (inner && dwellDistance() > 0) {
        this.#landingTarget = inner;
        const landing = gsap.timeline({
          scrollTrigger: {
            trigger: next,
            start: 'top top',
            end: () => `+=${openDistance() + dwellDistance()}`,
            scrub: true,
            invalidateOnRefresh: true,
          },
        });
        landing.fromTo(inner, { y: () => headerHeight() }, { y: () => headerHeight(), duration: 0.6, ease: 'none' }, 0);
        landing.to(inner, { y: 0, duration: 0.4, ease: 'power1.inOut' }, 0.6);
        this.#landing = landing;
      }
    } else {
      this.#trigger.vars.onLeave = settle;
    }

    // A load that restores a position beyond the chapter: settle at once.
    const last = this.#nextPin || this.#trigger;
    if (last && last.progress >= 1) {
      settle();
    }
  }
}

if (!customElements.get('manifesto-portal')) {
  customElements.define('manifesto-portal', ManifestoPortal);
}
